import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Fade, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  User_CreateUpdateUserInput,
  useUser_RemoveStripePaymentMethodMutation,
  useUser_UpdateUserMutation,
} from 'queries';
import * as yup from 'yup';

import { Button } from 'common/button';
import FormInputErrorMessage from 'common/form-input-error-message';
import PaymentOnFileLabel from 'common/payment-on-file-label';
import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import ProfileForm from 'pages/common/profile/profile-form';
import ProfilePath from 'pages/common/profile/profile-path';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import useCurrentUser from 'utils/useCurrentUser';

import ProfileAvatar from './components/profile-avatar';

const useStyles = makeStyles(() => ({
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  title: {
    color: '#8DA8B2',
  },
  subtitle: {
    color: '#096094',
    width: '55vw',
    marginTop: '4vh',
    marginBottom: '4vh',
  },
  grid: {
    backgroundColor: '#ffffff',
    marginTop: '20px',
    display: 'flex',
    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.25)',
    minHeight: '50vh',
    maxWidth: '100%',
  },
  divider: {
    backgroundColor: '#F5DE76',
    marginTop: '1vh',
    marginBottom: '4vh',
    width: '45vw',
    position: 'relative',
  },
  saveButton: {
    backgroundColor: '#F5DE76',
  },
  cancelButton: {
    color: '#ffffff',
    backgroundColor: '#8DA8B2',
    marginLeft: '15px',
  },
  text: {
    color: 'black',
  },
  buttonContainer: {
    float: 'right',
    marginBottom: '10vh',
    width: '45vw',
  },
  parentRoute: {
    color: '#8DA8B2',
    cursor: 'pointer',
  },
  routeIconContainer: {
    verticalAlign: 'middle',
  },
  portalContainer: {
    marginBottom: '50px',
  },
  formActionbuttons: {
    padding: '20px',
    paddingRight: '0',
    textAlign: 'right',
  },
  cardOnFile: {
    marginLeft: '2vh',
    marginBottom: '2vh',
  },
}));

const schema = yup.object().shape({
  firstName: yup.string().required('Required'),
  lastName: yup.string().required('Required'),
  email: yup.string().email().required('Required'),
  phone: yup.string().required('Please enter a phone number'),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  password: yup.lazy((value: any): any => {
    if (value) {
      return yup
        .string()
        .matches(
          /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
          'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and one special case Character',
        );
    }
    return yup.string();
  }),
  confirmPassword: yup.string().when('password', ([password], schema) => {
    if (password) {
      return schema.required('Required').oneOf([yup.ref('password')], 'Passwords must match');
    }
    return schema;
  }),
  enableStripePayments: yup.boolean(),
});

const MyProfile = () => {
  const classes = useStyles();
  const history = useHistory();

  const { portalId } = useParams<Record<string, string>>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [files, setLogo] = useState<any>();
  const [paymentMethodError, setPaymentMethodError] = useState('');

  const currentUser = useCurrentUser();

  const [updateUser, { loading: updateUserExecuting }] = useUser_UpdateUserMutation({
    onCompleted: () => {
      showToast({ message: 'Profile saved.', severity: NotificationSeverity.SUCCESS });
    },
    onError(error) {
      showAlert({
        message: error.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const formMethods = useForm<User_CreateUpdateUserInput>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    values: {
      firstName: currentUser?.firstName || '',
      lastName: currentUser?.lastName || '',
      email: currentUser?.email || '',
      phone: currentUser?.phone || '',
      password: '',
      confirmPassword: '',
      isConcierge: currentUser?.isConcierge ? true : false,
      isSuperAdmin: currentUser?.isSuperAdmin ? true : false,
      enableStripePayments: currentUser?.enableStripePayments ? true : false,
    },
  });

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = formMethods;

  const submitForm = (formData: User_CreateUpdateUserInput) => {
    updateUser({
      variables: {
        input: {
          ...formData,
          id: currentUser!.id,
          avatar: files,
        },
      },
    });
  };

  const [removeStripePaymentMethod, { loading: loadingRemoveStripePaymentMethod }] =
    useUser_RemoveStripePaymentMethodMutation({
      onError: (e) => {
        setPaymentMethodError(e.message);
      },
    });

  if (!currentUser) {
    return <Spinner />;
  }

  const removeStripeCard = () => {
    removeStripePaymentMethod({
      variables: { id: currentUser.id! },
    });
  };

  return (
    <>
      <HomeLayout showPortalName={false}>
        <Fade in={true}>
          <Grid className={classes.container}>
            <ProfilePath path="Edit My Profile" />
            <Grid className={classes.grid}>
              <Grid container>
                <Grid xs={12} item md={4} lg={2}>
                  <ProfileAvatar
                    setAvatar={setLogo}
                    avatarUrl={currentUser.avatarUrl || undefined}
                  />
                </Grid>
                <Grid xs={12} item md={8} lg={9}>
                  <Typography className={classes.subtitle} variant="h5">
                    Personal Information
                  </Typography>
                  <FormProvider {...formMethods}>
                    <form onSubmit={handleSubmit(submitForm)}>
                      <ProfileForm />
                      <Grid xs={12} className={classes.formActionbuttons}>
                        <Button
                          type="submit"
                          className={classes.saveButton}
                          disabled={(!isDirty && !files) || isSubmitting || updateUserExecuting}
                        >
                          Save Changes
                        </Button>
                        <Button
                          className={classes.cancelButton}
                          onClick={() => {
                            if (portalId) {
                              history.push(`/home/${portalId}/home`);
                              return;
                            }

                            history.push(`/home`);
                          }}
                        >
                          Cancel
                        </Button>
                      </Grid>
                    </form>
                  </FormProvider>
                  {currentUser.enableStripePayments && (
                    <div>
                      <Typography className={classes.subtitle} variant="h5">
                        Payment Information
                      </Typography>
                      <Grid xs={12} item className={classes.cardOnFile}>
                        {currentUser.stripeCardLabel ? (
                          <div>
                            <PaymentOnFileLabel stripeCardLabel={currentUser.stripeCardLabel} />{' '}
                            <Button
                              onClick={removeStripeCard}
                              size="small"
                              disabled={loadingRemoveStripePaymentMethod}
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <div>no card on file</div>
                        )}
                      </Grid>
                      <FormInputErrorMessage error={paymentMethodError} />
                    </div>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Fade>
      </HomeLayout>
    </>
  );
};

export default MyProfile;
